import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  TextField,
  Button,
  Container,
  Typography,
  Alert,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as routes from "../../../routes/constants";
import { postAuth } from "../../../api/user";

interface FormInputs {
  email: string;
  password: string;
}

export const LogInForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<FormInputs>({ mode: "onChange" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<FormInputs> = async (values) => {
    setIsSubmitting(true);
    setLoading(true);
    const { response, error } = await postAuth(values);
    setLoading(false);

    if (!error) {
      if (response && "token" in response) {
        setSubmissionError(null);
        localStorage.setItem("token", response.token);
        reset();
        navigate(routes.MAIN_SCREEN_ROUTE);
      } else {
        setSubmissionError(`Response: ${response || "empty"}`);
      }
    } else if ("status" in error) {
      response && "error" in response
        ? setSubmissionError(response.error)
        : setSubmissionError(
            `Response: ${response || "empty"}.
            Error: ${error.status} ${error.statusText}.`
          );
    } else {
      setSubmissionError(`Something went wrong: ${error.text}`);
    }
    setIsSubmitting(false);
  };

  return (
    <Container disableGutters maxWidth="sm">
      {loading ? (
        <Backdrop
          open={true}
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : null}
      <Typography variant="h5" align="center">
        ログイン
      </Typography>
      <form
        onSubmit={handleSubmit(onSubmit)}
        onChange={() => {
          setSubmissionError(null);
        }}
      >
        <TextField
          label="メールアドレス"
          {...register("email", {
            required: "* 必須",
            pattern: {
              value: /^\S+@\S+$/i,
              message: "無効なメールアドレス",
            },
          })}
          error={!!errors.email}
          helperText={errors.email?.message}
          fullWidth
          margin="normal"
          disabled={isSubmitting}
        />
        <TextField
          label="パスワード"
          type="password"
          {...register("password", {
            required: "* 必須",
            minLength: {
              value: 1,
              message: "パスワードを空にすることはできません",
            },
          })}
          error={!!errors.password}
          helperText={errors.password?.message}
          fullWidth
          margin="normal"
          disabled={isSubmitting}
        />
        {submissionError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {submissionError}
          </Alert>
        )}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!isValid || isSubmitting}
        >
          ログイン
        </Button>
      </form>
    </Container>
  );
};

export default LogInForm;
